import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import { Box } from '@mui/material';
import { styled } from '@mui/material';

import DeleteIcon from '~/assets/icons/svgs/trash-bin.svg?react';
import Accordion from '~/components/ui/Accordion/Accordion';
import { Button } from '~/components/ui/button';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { DESKTOP_MIN_WIDTH } from '~/constants/common';
import { COLORS } from '~/styles/colors';
import { VCardPlusData } from '~/types/qrCode';
import { isMaxLength } from '~/utils/common';

type VCardPlusCompaniesProps = {
  createVCardPlusCompany?: () => void;
  vCardPlusData: VCardPlusData;
  deleteVCardPlusCompany?: () => void;
  updateVCardPlusCompany?: () => void;
};

export default function VCardPlusCompanies({
  createVCardPlusCompany,
  vCardPlusData,
  deleteVCardPlusCompany,
  updateVCardPlusCompany,
}: VCardPlusCompaniesProps) {
  const { t } = useTranslation();

  const [companiesValues, setCompaniesValues] = useState(vCardPlusData?.companies || []);

  const handleAddCompany = (event) => {
    event.stopPropagation();

    createVCardPlusCompany({
      name: '',
      profession: '',
      position: vCardPlusData?.companies?.length || 0,
      microsite: vCardPlusData?.id,
    });
  };

  const handleDeleteCompany = (id, event) => {
    event.stopPropagation();
    setCompaniesValues((prevState) => prevState.filter((item) => item.id !== id));
    deleteVCardPlusCompany(id);
  };

  const handleChangeNameOrProfession = ({ field, id, value }) => {
    setCompaniesValues((prevState) =>
      prevState.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            [field]: value,
          };
        }

        return item;
      }),
    );
  };

  const handleSendCompanyField = ({ field, id, value }) => {
    updateVCardPlusCompany({
      body: {
        [field]: value,
      },
      companyId: id,
    });
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd = (result) => {
    if (result.destination) {
      const items = reorder(companiesValues, result.source.index, result.destination.index);
      setCompaniesValues(items);
      handleChangeCompanyList(items);
    }
  };

  const handleChangeCompanyList = (items) => {
    items.forEach((item, index) => {
      updateVCardPlusCompany({
        body: {
          position: index,
        },
        companyId: item.id,
      });
    });
  };

  useEffect(() => {
    if (vCardPlusData?.companies?.length > companiesValues.length) {
      setCompaniesValues(vCardPlusData?.companies);
    }
  }, [vCardPlusData?.companies?.length]);

  return (
    <Accordion
      variant="child"
      summary={t('template5:companies')}
      defaultExpanded
      details={
        <>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
              {(provided) => {
                return (
                  <Box
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
                  >
                    {vCardPlusData?.companies?.map((item, index) => {
                      return (
                        <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                          {(provided) => {
                            const name = companiesValues[index]?.name || '';
                            const profession = companiesValues[index]?.profession || '';

                            return (
                              <Box ref={provided.innerRef} {...provided.draggableProps}>
                                <StyledAccordion
                                  defaultExpanded
                                  sx={{ boxShadow: 'none', border: `1px solid ${COLORS.grey200}`, padding: '16px' }}
                                  summary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                      <Text variant="t16Regular" sx={{ whiteSpace: 'pre' }}>
                                        {`${t('other:company')} ${index + 1}`}
                                      </Text>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'right',
                                          width: '100%',
                                          marginRight: '16px',
                                          gap: '16px',
                                        }}
                                      >
                                        <Box {...provided.dragHandleProps} sx={{ cursor: 'pointer' }}>
                                          <MenuIcon />
                                        </Box>
                                        <DeleteIcon onClick={(e) => handleDeleteCompany(item.id, e)} />
                                      </Box>
                                    </Box>
                                  }
                                  details={
                                    <StyledDetailsContainer>
                                      <TextInput
                                        fullWidth
                                        value={name}
                                        helperText={isMaxLength(name, 100)}
                                        inputProps={{ maxLength: 100 }}
                                        label={t('other:name')}
                                        placeholder={t('template2:writeHere')}
                                        onBlur={() =>
                                          handleSendCompanyField({
                                            field: 'name',
                                            id: item.id,
                                            value: name,
                                          })
                                        }
                                        onChange={(e) => {
                                          handleChangeNameOrProfession({
                                            field: 'name',
                                            id: item.id,
                                            value: e.target.value,
                                          });
                                        }}
                                      />
                                      <TextInput
                                        fullWidth
                                        value={profession}
                                        helperText={isMaxLength(profession, 100)}
                                        inputProps={{ maxLength: 100 }}
                                        label={t('other:profession')}
                                        onBlur={() =>
                                          handleSendCompanyField({
                                            field: 'profession',
                                            id: item.id,
                                            value: profession,
                                          })
                                        }
                                        onChange={(e) => {
                                          handleChangeNameOrProfession({
                                            field: 'profession',
                                            id: item.id,
                                            value: e.target.value,
                                          });
                                        }}
                                      />
                                    </StyledDetailsContainer>
                                  }
                                />
                              </Box>
                            );
                          }}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </Box>
                );
              }}
            </Droppable>
          </DragDropContext>
          <Button
            type="primary-outlined"
            endIcon={<AddIcon />}
            onClick={handleAddCompany}
            size="xs"
            sx={{ marginTop: '24px' }}
          >
            {t('template5:addCompany')}
          </Button>
        </>
      }
    />
  );
}

export const StyledAccordion = styled(Accordion)(() => ({
  '.MuiAccordionSummary-content': {
    margin: 0,
  },
  '.MuiAccordionSummary-root': {
    minHeight: 0,
  },
}));

export const StyledDetailsContainer = styled('div')(({ theme }) => ({
  marginTop: '16px',
  display: 'flex',
  gap: '16px',
  [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
    flexDirection: 'column',
  },
}));
